import _ from 'lodash';
import * as constants from '../constants';

const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
// const log = require('debug')('@mycure/vue-plugins/' + nms);

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').Account} Account
 * @typedef {import('./state').AccountConfiguration} AccountConfiguration
 * @typedef {import('./state').PersonalDetails} PersonalDetails
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} GetAccountConfigActionOpts
 * @property {string} uid
 * @property {boolean} $forceFetch - use only for refresh buttons
 *
 * fetch account-specific configurations
 * @callback GetAccountConfigAction
 * @param {ActionContext} ctx
 * @param {GetAccountConfigActionOpts} opts
 * @returns {Promise<AccountConfiguration>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetAccountConfigAction}
 */
export const getAccountConfig = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  if (!opts.uid) throw new Error(`${nms}: Uid required`);

  // check cache
  const cached = ctx.state.accounts[opts.uid];
  if (cached && cached.accountConfig && !opts.$forceFetch) return cached.accountConfig;

  // fetch
  const accountConfig = await sdk.account.configurations().get(opts.uid);

  // cache
  if (accountConfig) ctx.commit('updateInAccounts', { upsert: true, item: { uid: opts.uid, accountConfig } });

  return accountConfig;
};

/**
 * @typedef {Object} UpdateAccountConfigActionOpts
 * @property {string} uid
 * @property {Object.<string,any>} emr update payload
 *
 * update account-specific configurations
 * @callback UpdateAccountConfigAction
 * @param {ActionContext} ctx
 * @param {UpdateAccountConfigActionOpts} opts
 * @returns {Promise<AccountConfiguration>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {UpdateAccountConfigAction}
 */
export const updateAccountConfig = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  if (!opts.uid) throw new Error(`${nms}: Uid required`);

  // execute
  const { uid, ...data } = opts;
  if (!data) throw new Error(`${nms}: Update payload required`);
  if (_.isEmpty(data)) throw new Error(`${nms}: Empty update payload`);
  const accountConfig = await sdk.account.configurations().update(uid, data);

  // cache
  if (accountConfig) ctx.commit('updateInAccounts', { upsert: true, item: { uid: opts.uid, accountConfig } });

  return accountConfig;
};

/**
 * @typedef {Object} GetPersonalDetailsActionOpts
 * @property {string} uid
 * @property {string[]} uids
 * @property {string[]} fields
 * @property {boolean} $forceFetch - use only for refresh buttons
 *
 * @callback GetPersonalDetailsAction
 * @param {ActionContext} ctx
 * @param {GetPersonalDetailsActionOpts} opts
 * @returns {Promise<PersonalDetails>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetPersonalDetailsAction}
 */
export const getPersonalDetails = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  if (!opts.uid && !opts.uids) throw new Error(`${nms}: Uid required`);

  // check cache
  if (opts.uid) {
    const cached = ctx.state.accounts[opts.uid];
    if (cached && cached.personalDetails && !opts.$forceFetch) return cached.personalDetails;

    // fetch
    const personalDetails = await sdk.personalDetails().get(opts.uid);

    // cache
    if (personalDetails) ctx.commit('updateInAccounts', { upsert: true, item: { uid: opts.uid, personalDetails } });

    return personalDetails;
  } else if (opts.uids) {
    const { items } = await sdk.personalDetails().find({ id: { $in: opts.uids } });
    return items;
  }
};

/**
 * @typedef {Object} CreateAccountActionOpts
 * @property {string} email
 * @property {string} password
 * @property {Object.<string,any>} personalDetails
 *
 * @callback CreateAccountAction
 * @param {ActionContext} ctx
 * @param {CreateAccountActionOpts} opts
 * @returns {Promise<Account>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CreateAccountAction}
 */
export const createAccount = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  if (!opts.email) throw new Error(`${nms}: Email required`);
  if (!opts.password) throw new Error(`${nms}: Password required`);

  // fetch
  const account = await sdk.accounts().create(_.pick(opts, 'email', 'password', 'personalDetails'));

  // cache
  ctx.commit('updateInAccounts', { upsert: true, item: account });

  return account;
};

/**
 * @typedef {Object} CheckEmailAvailabilityActionOpts
 * @property {string} email
 *
 * @callback CheckEmailAvailabilityAction
 * @param {ActionContext} ctx
 * @param {CheckEmailAvailabilityActionOpts} opts
 * @returns {Promise<boolean>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CheckEmailAvailabilityAction}
 */
export const checkEmailAvailability = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  if (!opts.email) throw new Error(`${nms}: Email required`);
  const isAvailable = await sdk.auth().checkUniqueIdentity('email', opts.email);
  return isAvailable;
};
